import {colors} from 'patient-ping-remedy/packages/theme';
import Icon, {ICONS} from 'patient-ping-remedy/packages/icon';
import ToolTip from 'patient-ping-remedy/packages/tool-tip';
import {CareJourneyAttachment, CareJourneyFaxStatus} from '@/api/dto/care-journey';
import DisplayHelpers from '@/helpers/display_helpers';

type Props = {
  careJourneyAttachment: CareJourneyAttachment;
}

// WAITING is deliberately absent: it means the fax has not been submitted yet, so there is no
// delivery outcome to report and no icon is shown.
const STATUS_MAPPING = {
  [CareJourneyFaxStatus.IN_PROGRESS]: {
    message: 'In progress sending',
    iconClass: ICONS.spinner,
    iconColor: colors.blue4,
    spin: true,
    transitionLabel: null,
  },
  [CareJourneyFaxStatus.COMPLETE]: {
    message: 'Successfully sent',
    iconClass: ICONS.check,
    iconColor: colors.green5,
    spin: false,
    transitionLabel: 'Delivered on',
  },
  [CareJourneyFaxStatus.FAILED]: {
    message: 'Failed to send',
    iconClass: ICONS['exclamation-triangle'],
    iconColor: colors.red5,
    spin: false,
    transitionLabel: 'Failed on',
  },
};

export default function AttachmentStatus({ careJourneyAttachment }: Props) {
  const fax = careJourneyAttachment.fax;
  const externalStatus = fax?.externalStatus;
  const mapping = externalStatus && externalStatus in STATUS_MAPPING
    ? STATUS_MAPPING[externalStatus as keyof typeof STATUS_MAPPING]
    : null;

  if (!fax || !mapping) {
    return null;
  }

  const destination = fax.fax ? ` to ${DisplayHelpers.formatPhoneNumber(fax.fax)}` : '';

  return (
    <ToolTip
      html={<>
        <span>{mapping.message}{destination}</span>
        {externalStatus === CareJourneyFaxStatus.FAILED && fax.externalFailReason &&
          <span style={{ overflowWrap: 'break-word', wordBreak: 'normal' }}>{fax.externalFailReason}</span>
        }
        {fax.sentAt &&
          <span>Sent on {DisplayHelpers.formatDateTimeLong(fax.sentAt, true)}</span>
        }
        {mapping.transitionLabel && fax.statusUpdatedAt &&
          <span>
            {mapping.transitionLabel} {DisplayHelpers.formatDateTimeLong(fax.statusUpdatedAt, true)}
          </span>
        }
        {fax.externalId && <span>ID: {fax.externalId}</span>}
      </>}
      type="dark"
    >
      <Icon
        iconClass={mapping.iconClass}
        color={mapping.iconColor}
        iconSize="1x"
        weight="far"
        spin={mapping.spin}
      />
    </ToolTip>
  );
}
